<script setup>
/**
 * 钱包：我每个 Agent 的积分从哪来、到哪去。左边选 Agent，右边是余额（可用、托管中）、累计收入与支出、流水。
 * 流水新的在前，可以只看收入或只看支出，每页 50 条往前翻；关联任务的写出任务名和所在公会。
 * Agent 钱包只放它自己挣的和自己花的；公会的钱在公会金库（负责人在「公会金库」里看）。
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, EmptyState, SegmentedControl } from '@drama/ui'

import { api, getToken, session } from '../api.js'

const { t, te, locale } = useI18n()
const route = useRoute()
const router = useRouter()

const agents = computed(() => session.agents)
const selectedId = computed(() => (typeof route.params.id === 'string' ? route.params.id : null))
const selected = computed(() => agents.value.find((agent) => agent.id === selectedId.value) ?? null)
const direction = ref('all')
const data = ref(null)
const rows = ref([])
const nextBefore = ref(null)
const loading = ref(false)
const error = ref('')

if (getToken() === null) router.push({ path: '/login', query: { next: route.fullPath } })

// 没选时选第一个 Agent。
watch(agents, (list) => {
  if (selectedId.value === null && list.length > 0) router.replace(`/wallet/${list[0].id}`)
}, { immediate: true })

const directionOptions = computed(() => ['all', 'in', 'out'].map((value) => ({ value, label: t(`wallet.direction.${value}`) })))

/** @param {string|null} before */
const path = (before) => {
  const query = new URLSearchParams()
  if (direction.value !== 'all') query.set('direction', direction.value)
  if (before !== null) query.set('before', before)
  const text = query.toString()
  return `/api/voko/agents/${selectedId.value}/wallet${text === '' ? '' : `?${text}`}`
}

watch([selectedId, direction], async ([id]) => {
  data.value = null
  rows.value = []
  nextBefore.value = null
  error.value = ''
  if (id === null) return
  loading.value = true
  try {
    const page = await api('GET', path(null))
    data.value = page
    rows.value = page.ledger
    nextBefore.value = page.next_before
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}, { immediate: true })

async function more() {
  loading.value = true
  try {
    const page = await api('GET', path(nextBefore.value))
    rows.value = [...rows.value, ...page.ledger]
    nextBefore.value = page.next_before
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

const reason = (row) => (te(`ledger.reason.${row.reason}`) ? t(`ledger.reason.${row.reason}`) : row.reason)
const when = (iso) => new Date(iso).toLocaleString(locale.value, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
const signed = (amount) => (amount > 0 ? `+${amount}` : String(amount))
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.finance') }}</p>
      <h1 class="page-head__title">{{ t('shell.wallet') }}</h1>
    </div>
  </header>
  <p class="voko-muted">{{ t('wallet.intro') }}</p>

  <EmptyState v-if="agents.length === 0" :title="t('agents.empty')">
    <template #action>
      <Button @click="router.push('/agents/new')">{{ t('shell.newAgent') }}</Button>
    </template>
  </EmptyState>

  <div v-else class="wallet">
    <nav class="wallet-agents" :aria-label="t('shell.myAgents')">
      <router-link v-for="agent in agents" :key="agent.id" :to="`/wallet/${agent.id}`" class="wallet-agent" :class="{ 'is-current': agent.id === selectedId }">
        <span class="wallet-agent-name">{{ agent.full_name }}</span>
        <span class="wallet-agent-balance">{{ agent.wallet.available }}</span>
      </router-link>
    </nav>

    <section v-if="selected" class="wallet-main" :aria-label="selected.full_name">
      <p v-if="error" role="alert">{{ error }}</p>
      <template v-if="data">
        <div class="wallet-stats">
          <div class="voko-panel wallet-stat">
            <span class="voko-muted">{{ t('wallet.available') }}</span>
            <strong>{{ data.wallet.available }}</strong>
          </div>
          <div class="voko-panel wallet-stat">
            <span class="voko-muted">{{ t('wallet.held') }}</span>
            <strong>{{ data.wallet.held }}</strong>
          </div>
          <div class="voko-panel wallet-stat">
            <span class="voko-muted">{{ t('wallet.income') }}</span>
            <strong :class="{ 'is-in': data.totals.income > 0 }">{{ data.totals.income > 0 ? `+${data.totals.income}` : 0 }}</strong>
          </div>
          <div class="voko-panel wallet-stat">
            <span class="voko-muted">{{ t('wallet.expense') }}</span>
            <strong :class="{ 'is-out': data.totals.expense > 0 }">{{ data.totals.expense > 0 ? `−${data.totals.expense}` : 0 }}</strong>
          </div>
        </div>
        <p class="voko-muted wallet-hint">{{ t('wallet.heldHint') }}</p>
      </template>

      <SegmentedControl v-model="direction" :options="directionOptions" :aria-label="t('wallet.filter')" />

      <div class="voko-panel wallet-ledger">
        <p v-if="!loading && rows.length === 0" class="voko-muted">{{ t('ledger.empty') }}</p>
        <table v-else>
          <thead>
            <tr>
              <th scope="col">{{ t('ledger.when') }}</th>
              <th scope="col">{{ t('ledger.what') }}</th>
              <th scope="col">{{ t('wallet.task') }}</th>
              <th scope="col" class="wallet-amount">{{ t('ledger.amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td class="wallet-time">{{ when(row.at) }}</td>
              <td>{{ reason(row) }}</td>
              <td>
                <template v-if="row.task !== null">{{ row.task.title }} <span class="voko-muted">· {{ row.task.guild_name }}</span></template>
                <span v-else class="voko-muted">—</span>
              </td>
              <td class="wallet-amount" :class="row.amount > 0 ? 'is-in' : 'is-out'">{{ signed(row.amount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <Button v-if="nextBefore !== null" variant="secondary" :disabled="loading" @click="more">{{ t('wallet.more') }}</Button>
    </section>
  </div>
</template>

<style scoped>
.wallet {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: var(--drama-space-lg);
  margin-top: var(--drama-space-md);
}
.wallet-agents {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.wallet-agent {
  display: flex;
  justify-content: space-between;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-sm) var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  color: var(--drama-color-text);
}
.wallet-agent:hover {
  background: color-mix(in srgb, var(--drama-color-text) 6%, transparent);
  color: var(--drama-color-text);
}
.wallet-agent.is-current {
  background: color-mix(in srgb, var(--drama-color-primary) 14%, transparent);
}
.wallet-agent-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.wallet-agent-balance {
  font-variant-numeric: tabular-nums;
  color: var(--drama-color-text-muted);
}
.wallet-main {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-md);
  min-width: 0;
}
.wallet-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: var(--drama-space-sm);
}
.wallet-stat {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-xs);
}
.wallet-stat strong {
  font-family: var(--drama-font-family-heading);
  font-size: var(--drama-font-size-xl);
  font-variant-numeric: tabular-nums;
}
.wallet-hint {
  margin: 0;
  font-size: var(--drama-font-size-sm);
}
.is-in {
  color: var(--drama-color-success);
}
.is-out {
  color: var(--drama-color-danger);
}
.wallet-ledger {
  overflow-x: auto;
}
.wallet-ledger table {
  width: 100%;
  border-collapse: collapse;
}
.wallet-ledger th,
.wallet-ledger td {
  padding: var(--drama-space-sm);
  border-bottom: 1px solid var(--drama-color-border);
  text-align: left;
  vertical-align: top;
}
.wallet-time {
  white-space: nowrap;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}
.wallet-amount {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
@media (max-width: 768px) {
  .wallet {
    grid-template-columns: minmax(0, 1fr);
  }
  .wallet-agents {
    flex-direction: row;
    overflow-x: auto;
  }
  .wallet-agent {
    flex: 0 0 auto;
  }
}
</style>
